import { cloneDeep } from 'lodash';
import { assignProps, removeEmpty, previousCarouselIndex } from '@/utils/resultsHelper';
import withDraft from '@/utils/draftHelper';
import {
  findFormSectionByFieldName,
  newFormLink,
  newFormObject,
  pruneFormFieldErrors,
  safeFormObjects,
  transformFormObjects,
} from '@/forms/state/state';
import actions from '../constants';
import { activeRequestFieldArray, requestLinksDialogState, requestObjectsDialogState } from './state';
import { pruneFieldErrors, findRequest } from '../../state/state';

const updateRequestFieldsObjectProperties = withDraft((draft, {
  activeFieldName,
  activeIndex,
  category,
  code,
  properties,
}) => {
  const item = findRequest(draft, { category, code });
  const field = safeFormObjects(item.fields[activeFieldName]);

  assignProps(field[activeIndex], properties);
  item.fields[activeFieldName] = field;
  item.isDirty = true;
});

const deleteRequestObject = withDraft((draft, action) => {
  const item = findRequest(draft, action);
  const items = safeFormObjects(item.fields[action.activeFieldName]);
  items.splice(action.activeIndex, 1);
  item.fields[action.activeFieldName] = items;
  item.isDirty = true;
});

const showRequestLinks = withDraft((draft, action) => {
  const item = findRequest(draft, action);

  const items = cloneDeep(item.fields[action.activeFieldName]);
  requestLinksDialogState(draft.requestFieldItems, safeFormObjects(items), true, action);
});

const showRequestObjects = withDraft((draft, action) => {
  const item = findRequest(draft, action);

  const items = cloneDeep(item.fields[action.activeFieldName]);
  requestObjectsDialogState(
    draft.requestFieldItems,
    safeFormObjects(items),
    item.isNew,
    action,
  );
});

const applyRequestObjects = withDraft((draft, action) => {
  const item = findRequest(draft, action);
  const items = transformFormObjects(removeEmpty(draft.requestFieldItems.items));
  const entity = findFormSectionByFieldName(action, draft.requestFieldItems.activeFieldName);
  item.fields[draft.requestFieldItems.activeFieldName] = items;
  item.saveErrors = pruneFormFieldErrors(item.saveErrors, {
    fields: [draft.requestFieldItems.activeFieldName],
    entity,
  });
  item.isDirty = true;
  draft.requestFieldItems = activeRequestFieldArray();
});

const addRequestLinkIfExists = ({ fields }, { sections, activeFieldName }) => {
  const field = fields[activeFieldName];
  if (!field) { return [{}]; }

  const items = safeFormObjects(cloneDeep(field));
  items.push(newFormLink({ sections }, activeFieldName, true));

  return items;
};

const addRequestLink = withDraft((draft, action) => {
  const items = addRequestLinkIfExists(findRequest(draft, action), action);
  draft.requestFieldItems.isDirty = true;
  requestLinksDialogState(draft.requestFieldItems, items, true, {
    ...action,
    activeIndex: items.length - 1,
  });
});

const addRequestObjectIfExists = ({ fields }, { sections, activeFieldName }) => {
  const field = fields[activeFieldName];
  if (!field) { return [{}]; }

  const items = safeFormObjects(cloneDeep(field));
  items.push(newFormObject({ sections }, activeFieldName, true));

  return items;
};

const addRequestObject = withDraft((draft, action) => {
  const items = addRequestObjectIfExists(findRequest(draft, action), action);
  draft.requestFieldItems.isDirty = true;
  requestObjectsDialogState(draft.requestFieldItems, items, true, {
    ...action,
    activeIndex: items.length - 1,
  });
});

const hideRequestObjects = withDraft((draft) => { draft.requestFieldItems = activeRequestFieldArray(); });

const updateActiveRequestObject = withDraft((draft, { index }) => {
  draft.requestFieldItems.activeIndex = index;
});

const updateRequestObjectsProperties = withDraft((draft, { properties }) => {
  assignProps(draft.requestFieldItems, properties);
});

const updateRequestObjectProperties = withDraft((draft, { properties }) => {
  const { items, activeIndex } = draft.requestFieldItems;

  const item = items[activeIndex];
  assignProps(item, properties);
  draft.requestFieldItems.isDirty = true;
  draft.requestFieldItems.saveErrors = pruneFieldErrors(draft.requestFieldItems.saveErrors, {
    properties,
    entity: activeIndex.toString(),
  });
});

const deleteActiveRequestObject = withDraft((draft) => {
  const { items, activeIndex } = draft.requestFieldItems;

  items[draft.requestFieldItems.activeIndex] = null;
  draft.requestFieldItems.activeIndex = previousCarouselIndex(items, activeIndex);
  draft.requestFieldItems.isDirty = true;
});

const insertRequestLink = withDraft((draft, action) => {
  const { items, activeFieldName, activeIndex } = draft.requestFieldItems;

  const newItem = newFormLink(action, activeFieldName, true);
  const itemsState = { ...draft.requestFieldItems, ...action, activeIndex: activeIndex + 1 };
  items.splice(activeIndex + 1, 0, newItem);
  requestLinksDialogState(draft.requestFieldItems, items, false, itemsState);
  draft.requestFieldItems.isDirty = true;
});

const insertRequestObject = withDraft((draft, action) => {
  const { items, activeFieldName, activeIndex } = draft.requestFieldItems;

  const newItem = newFormObject(action, activeFieldName, true);
  const itemsState = { ...draft.requestFieldItems, ...action, activeIndex: activeIndex + 1 };
  items.splice(activeIndex + 1, 0, newItem);
  requestObjectsDialogState(draft.requestFieldItems, items, false, itemsState);
  draft.requestFieldItems.isDirty = true;
});

export default {
  [actions.ADD_REQUEST_LINK]: addRequestLink,
  [actions.ADD_REQUEST_OBJECT]: addRequestObject,
  [actions.APPLY_REQUEST_OBJECTS]: applyRequestObjects,
  [actions.DELETE_ACTIVE_REQUEST_OBJECT]: deleteActiveRequestObject,
  [actions.DELETE_REQUEST_OBJECT]: deleteRequestObject,
  [actions.HIDE_REQUEST_OBJECTS]: hideRequestObjects,
  [actions.INSERT_REQUEST_LINK]: insertRequestLink,
  [actions.INSERT_REQUEST_OBJECT]: insertRequestObject,
  [actions.SHOW_REQUEST_LINKS]: showRequestLinks,
  [actions.SHOW_REQUEST_OBJECTS]: showRequestObjects,
  [actions.UPDATE_ACTIVE_REQUEST_OBJECT]: updateActiveRequestObject,
  [actions.UPDATE_REQUEST_FIELDS_OBJECT_PROPERTIES]: updateRequestFieldsObjectProperties,
  [actions.UPDATE_REQUEST_OBJECT_PROPERTIES]: updateRequestObjectProperties,
  [actions.UPDATE_REQUEST_OBJECTS_PROPERTIES]: updateRequestObjectsProperties,
};
